import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useState } from 'react'
import { Building2, MapPin, ChevronRight } from 'lucide-react'
import { getDirectoryTowns, getDirectoryNeighbourhoods, getDirectoryPlumbersForCounty, type DirectoryExtraFilters } from '../../lib/api'
import { DIRECTORY_PLUMBERS_PAGE_SIZE, DIRECTORY_MAP_PLUMBER_LIMIT } from '../../lib/directoryConstants'
import { useDirectoryPlumbersInfinite } from '../../hooks/useDirectoryPlumbersInfinite'
import { useDirectoryMode } from '../../hooks/useDirectoryMode'
import { PageWrapper } from '../../components/layout/PageWrapper'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Seo } from '../../components/Seo'
import { directoryCanonicalPath } from '../../lib/directoryCanonical'
import { NavCardGridSkeleton } from '../../components/ui/Skeletons'
import { DirectorySidebar } from '../../components/directory/DirectorySidebar'
import { DirectoryPlumberList } from '../../components/directory/DirectoryPlumberList'
import { DIRECTORY_MAIN_GRID, DIRECTORY_PAGE_MAX, DIRECTORY_SIDEBAR_COL, slugToLabel } from './directoryUtils'
import { DirectorySearchBar } from '../../components/directory/DirectorySearchBar'
import { buildPlumberHref, toPlumberPins } from '../../lib/plumberPins'

export function DirectoryCounty() {
  const { countrySlug, countySlug } = useParams<{ countrySlug: string; countySlug: string }>()
  const { isEmergency, isGasSafe, isWaterSafe, basePath, label } = useDirectoryMode()
  const [extraFilters, setExtraFilters] = useState<DirectoryExtraFilters>({})

  const { data: towns, isLoading: loadingTowns } = useQuery({
    queryKey: ['directory', 'towns', countrySlug, countySlug, isEmergency, isGasSafe, isWaterSafe],
    queryFn: () => getDirectoryTowns(countrySlug!, countySlug!, isEmergency, isGasSafe, isWaterSafe),
    enabled: !!(countrySlug && countySlug),
  })

  const { data: areas } = useQuery({
    queryKey: ['directory', 'areas', countrySlug, countySlug, isEmergency, isGasSafe, isWaterSafe],
    queryFn: () => getDirectoryNeighbourhoods(countrySlug!, countySlug!, isEmergency, isGasSafe, isWaterSafe),
    enabled: !!(countrySlug && countySlug),
  })

  const fetchPlumbers = useCallback(
    (skip: number) =>
      getDirectoryPlumbersForCounty(countrySlug!, countySlug!, isEmergency, isGasSafe, isWaterSafe, {
        skip,
        limit: DIRECTORY_PLUMBERS_PAGE_SIZE,
      }, extraFilters),
    [countrySlug, countySlug, isEmergency, isGasSafe, isWaterSafe, extraFilters],
  )

  const {
    data: plumberPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: loadingPlumbers,
  } = useDirectoryPlumbersInfinite(
    ['directory', 'plumbers', 'county', countrySlug, countySlug, isEmergency, isGasSafe, isWaterSafe, extraFilters],
    fetchPlumbers,
    !!(countrySlug && countySlug),
  )

  const plumbers = plumberPages?.pages.flatMap(p => p.plumbers) ?? []
  const listTotal = plumberPages?.pages[0]?.total
  const emergencyTotal = plumberPages?.pages[0]?.emergencyTotal ?? 0

  const countryLabel = slugToLabel(countrySlug)
  const countyLabel  = slugToLabel(countySlug)

  const hrefCtx = { basePath, countrySlug: countrySlug!, countySlug: countySlug! }

  // Separate map query - fetches all plumbers regardless of active filters
  const { data: mapData } = useQuery({
    queryKey: ['directory', 'plumbers-map', 'county', countrySlug, countySlug, isEmergency, isGasSafe, isWaterSafe],
    queryFn: () => getDirectoryPlumbersForCounty(countrySlug!, countySlug!, isEmergency, isGasSafe, isWaterSafe, { skip: 0, limit: DIRECTORY_MAP_PLUMBER_LIMIT }, {}),
    enabled: !!(countrySlug && countySlug),
    staleTime: 5 * 60 * 1000,
  })
  const plumberPins = useMemo(
    () => toPlumberPins(mapData?.plumbers ?? [], p => buildPlumberHref(p, hrefCtx)),
    [mapData, basePath, countrySlug, countySlug],
  )

  const faqs = [
    {
      q: `How do I find ${isEmergency ? 'an emergency plumber' : isGasSafe ? 'a Gas Safe plumber' : isWaterSafe ? 'a WaterSafe plumber' : 'a plumber'} in ${countyLabel}?`,
      a: `Browse${listTotal ? ` our ${listTotal} listed` : ''} plumbers in ${countyLabel} by town, then by postcode. For an immediate callout, use FixDelivered's emergency request.`,
    },
    {
      q: `What plumbing services are available in ${countyLabel}?`,
      a: `Full range: emergency callouts, boiler repair, central heating, burst pipes, blocked drains, leak detection, gas safety certificates, and bathroom installations.`,
    },
  ]

  return (
    <PageWrapper>
      <Seo
        title={`${label} in ${countyLabel} | FixDelivered`}
        description={`${listTotal ? `${listTotal} verified` : 'Find'} ${label.toLowerCase()} in ${countyLabel}. Many available 24/7.`}
        path={directoryCanonicalPath(countrySlug, countySlug)}
        noindex={listTotal === 0}
        breadcrumbs={[
          { name: 'UK', url: basePath },
          { name: countryLabel, url: `${basePath}/${countrySlug}` },
          { name: countyLabel, url: `${basePath}/${countrySlug}/${countySlug}` },
        ]}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
        }}
      />

      <div className={DIRECTORY_PAGE_MAX}>
        <div className={DIRECTORY_MAIN_GRID}>
          <div className="min-w-0">
            <Breadcrumb items={[
              { label: countryLabel, href: `${basePath}/${countrySlug}` },
              { label: countyLabel },
            ]} />

            <div className="mt-6 mb-6">
              <h1 className="text-2xl font-bold text-gray-900">{label} in {countyLabel}</h1>
              {/* Reserve height so the plumber count loading in doesn't reflow the
                  page (the count comes from a separate, slower query -> CLS). */}
              <p className="text-gray-500 mt-1 text-sm min-h-[2.75rem] sm:min-h-[1.5rem]">
                {listTotal && listTotal > 0
                  ? `${listTotal} verified plumbers${emergencyTotal > 0 ? ` · ${emergencyTotal} available 24/7` : ''}. `
                  : ''}
                Select a town to browse local plumbers.
              </p>
              <DirectorySearchBar className="mt-3" />
            </div>

            {loadingTowns ? (
              <NavCardGridSkeleton count={6} />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(towns ?? []).map(t => (
                  <Link
                    key={t.townSlug}
                    to={`${basePath}/${countrySlug}/${countySlug}/${t.townSlug}`}
                    className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all p-5 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-4 w-4 text-gray-400" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 capitalize">{t.town}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{t.count} plumber{t.count !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-trust-400 transition-colors" />
                  </Link>
                ))}
              </div>
            )}

            {!loadingTowns && !towns?.length && (
              <div className="text-center py-16 text-gray-400">
                <p>No towns found in this county.</p>
                <Link to={`${basePath}/${countrySlug}`} className="text-trust-500 hover:underline text-sm mt-2 inline-block">
                  Back to {countryLabel}
                </Link>
              </div>
            )}

            {areas && areas.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Browse by Area</h2>
                  <Link to={`${basePath}/${countrySlug}/${countySlug}/areas`} className="text-xs text-trust-500 hover:underline">
                    View all {areas.length} areas
                  </Link>
                </div>
                <Link
                  to={`${basePath}/${countrySlug}/${countySlug}/areas`}
                  className="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all p-5 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-4 w-4 text-gray-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Named areas & districts</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {areas.length} areas - {areas.slice(0, 3).map(a => a.neighbourhood).join(', ')} and more
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-trust-400 transition-colors" />
                </Link>
              </div>
            )}

            <DirectoryPlumberList
              plumbers={plumbers}
              isLoading={loadingPlumbers}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              listTotal={listTotal}
              extraFilters={extraFilters}
              onFiltersChange={setExtraFilters}
              hrefBuilder={p => buildPlumberHref(p, hrefCtx)}
              heading={`Plumbers in ${countyLabel}`}
              emptyAreaLabel="this county"
            />
          </div>

          <div className={DIRECTORY_SIDEBAR_COL}>
            <DirectorySidebar
              count={listTotal}
              areaLabel={countyLabel}
              emergencyCount={emergencyTotal}
              plumberPins={plumberPins}
            />
          </div>
        </div>
      </div>
    </PageWrapper>
  )
}
